<template>
  <!--  https://vcalendar.io/examples/datepickers.html#date-buttons-->
  <div class="bg-white p-2 w-full border border-element-neutral rounded">
    <date-picker
      v-model="currentDate"
      mode="dateTime"
      :is24hr="true"
      :masks="masks"
      :popover="{ visibility: 'click' }"
      @update:modelValue="updateCurrentDate"
    >
      <template #default="{ inputValue, togglePopover, hidePopover }">
        <div class="flex flex-wrap">
          <button
            v-for="date in internalDates"
            ref="button"
            class="flex items-center text-sm font-normal text-white py-1 pl-4 m-1 rounded-lg bg-primary"
            @click.stop="dateSelected($event, date, togglePopover)"
          >
            {{ date.date ? format(date.date, 'dd.MM.yyyy HH:mm') : '' }}
            <svg
              class="w-9 h-4 ml-3 pl-2 pr-3 border-l"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              @click.stop="removeDate(date, hidePopover)"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </template>
    </date-picker>
    <button class="text-sm px-2 h-8" @click.stop="addDate">
      {{ $t('multiDatePicker.addDate') }}
    </button>
  </div>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { DatePicker } from 'v-calendar';
import {nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import { format } from 'date-fns';
import type { Variant } from './Field.vue';
import type { ValidatedTree } from '@/hooks/form';

interface Props {
  modelValue: Date[] | undefined;
  masks?: {
    input?: string|string[];
    inputDateTime?: string|string[];
    inputDateTime24hr?: string|string[];
    inputTime?: string|string[];
    inputTime24hr?: string|string[];
  };
  label?: string;
  placeholderFrom?: string;
  placeholderTo?: string;
  error?: string | ValidatedTree | undefined;
}

const emit = defineEmits(['update:modelValue']);
const props = withDefaults(defineProps<Props>(), {
  masks: () => ({
    input: 'DD.MM.YYYY',
    inputTime24hr: ['HH:mm:ss', 'HH:mm'],
    inputDateTime: ['DD.MM.YYYY HH:mm:ss', 'DD.MM.YYYY HH:mm', 'DD.MM.YYYY'],
    inputDateTime24hr: ['DD.MM.YYYY HH:mm:ss', 'DD.MM.YYYY HH:mm', 'DD.MM.YYYY'],
  }),
  modelValue: undefined,
});

interface DateType {
  date: Date;
}

const dates = useVModel(props, 'modelValue', emit, { passive: true });

const internalDates = ref<DateType[]>([]);

watch(
  internalDates,
  () => {
    dates.value = internalDates.value.map((item: DateType) => item.date);
  },
  { deep: true },
);

const updateDown = () => {
  internalDates.value = dates.value?.map((date: Date) => ({ date }));
};

updateDown();

watch(dates, (newVal, oldVal) => {
  if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
    updateDown();
  }
});

const selected = ref<Partial<DateType>>({ date: undefined });
const currentDate = ref<Date | undefined>(undefined);

const updateCurrentDate = (newVal: Date) => {
  selected.value?.date?.setTime(newVal.getTime());
};

const button = ref<HTMLButtonElement[]>([]);

const addDate = () => {
  internalDates.value.push({ date: new Date() });
  nextTick(() => {
    const btn = button.value[button.value.length - 1];
    btn.click();
  });
};

const removeDate = (date: DateType, hide: Function) => {
  internalDates.value = internalDates.value?.filter((el) => el !== date) ?? [];
  hide();
};

const dateSelected = (e: Event, date: DateType, toggle: Function) => {
  selected.value = date;
  currentDate.value = date.date;
  toggle({ ref: e.target });
};

// Apologies for the hack below, v-calendar is sh*t
const stopPopoverHide = (e) => {
  e.stopImmediatePropagation();
};

onMounted(() => {
  document.addEventListener('hide-popover', stopPopoverHide, {
    capture: true,
  });
})

onUnmounted(() => {
  document.removeEventListener('hide-popover', stopPopoverHide);
})
</script>

<style lang="scss" scoped>

</style>
